import { CheckIcon, CloseIcon, EditIcon } from "@chakra-ui/icons";
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Tag,
  Text,
  Flex,
  TagLabel,
  Tooltip,
  Textarea,
  IconButton,
  Button,
  Menu,
  MenuList,
  MenuItem,
  MenuButton,
  useDisclosure,
} from "@chakra-ui/react";
import { formatTime } from "constants/commonFunctions";
import { formatDate } from "constants/commonFunctions";
import { GenderTypes } from "constants/filterDropdownData";
import { CLASSIFIED_ROLE_TYPES } from "constants/filterDropdownData";
import { useEffect, useMemo, useState } from "react";
import { RiArrowDropDownLine } from "react-icons/ri";

const sumValues = (obj) =>
  Object.values(obj || {}).reduce(
    (acc, val) => (typeof val === "number" ? acc + val : acc),
    0
  );

const capitalize = (str) =>
  str
    ? str
        .replace(/_/g, " ")
        .replace(
          /\w\S*/g,
          (txt) => txt[0].toUpperCase() + txt.slice(1).toLowerCase()
        )
    : "";

const formatHighlights = (obj, categories) => {
  if (!obj?.total) return "-";

  const highlights = categories.map((cat) => {
    const sub = obj[cat];
    if (!sub) return null;

    const count = sumValues(sub);
    if (!count) return null;

    const details = Object.entries(sub)
      .filter(([k]) => k !== "total")
      .map(([k, v]) => `${capitalize(k)} (${v})`)
      .join(", ");

    return (
      <Box key={cat} as="span" mr={2}>
        ● &nbsp;
        <Text as="span" fontWeight="bold">
          {capitalize(cat)} ({count})
        </Text>
        {details && `: ${details}`}
      </Box>
    );
  });

  return highlights.filter(Boolean).length ? highlights : "-";
};

const formatSearches = (searches, categories) => {
  if (!searches?.total) return "-";

  const highlights = categories
    .map((cat) => {
      const total = sumValues(searches[cat]);
      return total > 0 ? `${capitalize(cat)} (${total})` : null;
    })
    .filter(Boolean);

  return highlights.length ? highlights.join(" ● ") : "-";
};

function formatPricing(num) {
  if (num == null || isNaN(num)) return "-";

  if (num >= 1e7) {
    return (num / 1e7).toFixed(2).replace(/\.?0+$/, "") + "Cr";
  } else if (num >= 1e5) {
    return (num / 1e5).toFixed(2).replace(/\.?0+$/, "") + "Lac";
  } else {
    return new Intl.NumberFormat("en-IN").format(num);
  }
}

const formatBudgets = (obj, parent = null, isRoot = true) => {
  if (!obj || typeof obj !== "object") return [];

  return Object.entries(obj).flatMap(([key, value]) => {
    if (isRoot && key === "budget") return [];

    if (key === "budget" && value && (value.min != null || value.max != null)) {
      const min = value.min != null ? formatPricing(value.min) : "0";
      const max = value.max != null ? formatPricing(value.max) : "NA";
      return [`(${min}-${max}) ${parent}`];
    }
    if (typeof value === "object" && value !== null) {
      return formatBudgets(value, key, false);
    }

    return [];
  });
};

const AdminComment = ({ value, onEditComment, id }) => {
  const [adminComment, setAdminComment] = useState(value);
  const [isEditing, setIsEditing] = useState(false);

  const updateAdminComment = () => {
    setIsEditing(false);
    onEditComment({
      comment: adminComment,
      status: "Add Comment",
      id,
    });
  };

  useEffect(() => {
    setAdminComment(value);
  }, [value]);

  return (
    <Tr
      bg="white"
      _hover={{ bg: "gray.100" }}
      transition="background-color 0.2s"
    >
      <Td fontWeight="medium" color="gray.800">
        Admin Comment
      </Td>
      <Td className="w-full">
        {!isEditing ? (
          <Flex direction="row" gap={2}>
            <Tooltip
              label={adminComment || ""}
              hasArrow
              placement="top"
              bg="gray.700"
              color="white"
            >
              <Text
                width="full"
                fontSize="sm"
                noOfLines={1}
                color="gray.800"
                cursor="pointer"
              >
                {adminComment || ""}
              </Text>
            </Tooltip>
            {onEditComment && (
              <Button
                size="sm"
                leftIcon={<EditIcon />}
                onClick={() => setIsEditing(true)}
              >
                Edit
              </Button>
            )}
          </Flex>
        ) : (
          <Flex align="flex-start" gap={2}>
            {/* Textarea grows to fill space */}
            <Textarea
              flex="1"
              value={adminComment}
              onChange={(e) => setAdminComment(e.target.value)}
              size="sm"
            />

            {/* Buttons take only the needed space */}
            <Flex direction="column" gap={2}>
              <IconButton
                aria-label="Save"
                icon={<CheckIcon />}
                size="sm"
                colorScheme="green"
                onClick={() => updateAdminComment()}
              />
              <IconButton
                aria-label="Cancel"
                icon={<CloseIcon />}
                size="sm"
                colorScheme="red"
                onClick={() => {
                  setAdminComment(value);
                  setIsEditing(false);
                }}
              />
            </Flex>
          </Flex>
        )}
      </Td>
    </Tr>
  );
};

const DropdownChip = ({
  value,
  options,
  label,
  onChange,
  disabled = false,
  onConfirm,
}) => {
  const { isOpen, onOpen, onClose, onToggle } = useDisclosure();

  const currentVal = useMemo(() => {
    const found = options.find(
      (item) => value === item.value || value === item
    );
    return found?.label || found || null;
  }, [value, options]);

  const handleSelect = (option) => {
    onChange(option.value || option);

    // ❗ Do NOT close here when onConfirm exists
    if (!onConfirm) {
      onClose(); // normal behavior when tick isn’t used
    }
  };

  const handleConfirm = () => {
    onConfirm?.();
    onClose(); // Close menu only on tick
  };

  return (
    <Menu isOpen={isOpen} onClose={onClose} closeOnSelect={!onConfirm}>
      <MenuButton
        as={Button}
        onClick={onToggle}
        rightIcon={<RiArrowDropDownLine />}
        variant="filled"
        bg="blue.500"
        colorScheme="blue"
        borderRadius="full"
        color="white"
        px={4}
        py={2}
        fontWeight="bold"
        boxShadow="sm"
        disabled={disabled}
        _hover={{ bg: "blue.600", boxShadow: "md" }}
      >
        {label}:{" "}
        {String(currentVal).charAt(0).toUpperCase() +
          String(currentVal).slice(1)}
      </MenuButton>

      <MenuList>
        {options.map((option, index) => {
          const isSelected = value === option || value === option.value;
          return (
            <MenuItem
              key={index}
              onClick={() => handleSelect(option)}
              fontWeight={isSelected ? "medium" : "normal"}
              color={isSelected ? "blue.600" : "gray.700"}
              bg={isSelected ? "blue.50" : "transparent"}
              _hover={{ bg: "blue.50" }}
            >
              {option.label || option}
            </MenuItem>
          );
        })}

        {onConfirm && (
          <IconButton
            aria-label="Save"
            icon={<CheckIcon />}
            size="xs"
            colorScheme="green"
            position="absolute"
            right="-6px"
            top="-6px"
            onClick={handleConfirm}
          />
        )}
      </MenuList>
    </Menu>
  );
};

/** 🔹 Main Component */
const JSONTable = ({
  data,
  tableData,
  onEditComment,
  adminRole,
  setAdminRole,
  gender,
  setGender,
  confirmClassification,
}) => {
  const { comment: adminComment } = tableData || {};
  const { categories, classification, activitySummary, geoSignals } =
    useMemo(() => {
      return {
        categories: ["sell", "rent", "pg", "coliving"],
        classification: data?.classification ?? {},
        activitySummary: data?.classification?.activity_summary || {},
        geoSignals: data?.classification?.geographic_signals || {},
      };
    }, [data]);

  /** Table Data */
  const tableRowData = useMemo(
    () => [
      {
        metric: "Total Activities",
        count: activitySummary.total_activities || 0,
        highlights:
          (activitySummary.total_activities || 0) > 10
            ? "High Volume"
            : "Low Volume",
      },
      {
        metric: "Ads Viewed",
        count: activitySummary.ads_viewed?.total || 0,
        highlights: formatHighlights(activitySummary.ads_viewed, categories),
      },
      {
        metric: "Ads Posted",
        count: activitySummary.ads_posted?.total || 0,
        highlights: formatHighlights(activitySummary.ads_posted, categories),
      },
      {
        metric: "Requests",
        count: activitySummary?.request_made?.length || 0,
        highlights:
          activitySummary?.request_made
            ?.map((item) => item?.type)
            ?.join(" ● ") || "-",
      },
      {
        metric: "Contacts Made",
        count: activitySummary.contacts_made?.total || 0,
        highlights: formatHighlights(activitySummary.contacts_made, categories),
      },
      {
        metric: "Searches",
        count: activitySummary.searches_performed?.total || 0,
        highlights: formatSearches(
          activitySummary.searches_performed,
          categories
        ),
      },
      {
        metric: "Budgets",
        count: "",
        highlights:
          formatBudgets(activitySummary?.searches_performed)?.join(" ● ") ||
          "-",
      },
      {
        metric: "Classified Location",
        count: "",
        highlights: (() => {
          const { city, locality } = classification?.classified_location || {};
          if (!city && !locality) return "-";
          return [city, locality].filter(Boolean).join(" ");
        })(),
      },
      {
        metric: "Orders",
        count: classification?.orders_summary?.total_orders || 0,
        highlights: classification?.orders_summary?.has_orders
          ? "Has Order"
          : "No Order Found",
      },
      {
        metric: "AI comment",
        count: "",
        highlights: classification?.comments,
      },
    ],
    [activitySummary, geoSignals, classification, categories, adminComment]
  );

  const { min, max } =
    classification.activity_summary?.searches_performed?.budget ?? {};

  /** Tag Fields */
  const fields = useMemo(
    () => [
      {
        key: "secondary_classification",
        label: "Secondary Role:",
        value: capitalize(classification.secondary_classification),
        color: "purple",
      },
      {
        key: "plan_data",
        label: "Active Plans:",
        value: tableData?.plan_names,
        color: "teal",
      },
      {
        key: "needs_manual_review",
        label: "Manual Review:",
        value: "Required",
        color: "red",
      },
      {
        key: "classified_property_type",
        label: "Property Type:",
        value: capitalize(classification?.classified_property_type),
        color: "yellow",
      },
      {
        key: "classified_ad_type",
        label: "Classified Ad Type:",
        value: capitalize(classification?.classified_ad_type),
        color: "orange",
      },
      {
        key: "platform",
        label: "Platform:",
        value: data?.user_info?.platform || tableData?.platform || "-",
        color: "gray",
      },
      {
        key: "budget",
        label: "Budget:",
        value:
          min != null || max != null
            ? `${min != null ? formatPricing(min) : "0"} - ${
                max != null ? formatPricing(max) : "max"
              }`
            : null,
        color: "teal",
      },
    ],
    [classification, min, max, data]
  );

  const heading = useMemo(() => {
    const { first_name, last_name, phone } = data?.user_info ?? {};
    const role = data?.classification?.user_role_selected;
    return `${capitalize(first_name)} ${capitalize(last_name)} (${capitalize(
      role
    )}) ${phone}`;
  }, [data]);

  const createdAt = data?.user_info?.created_at;

  return (
    <Flex w="full" direction="column" gap={4} mx="auto" p={4}>
      <Heading size="lg" color="gray.800">
        Activity: {heading}
      </Heading>

      {createdAt && (
        <Text fontSize="md" color="gray.500">
          Onboarded on {formatDate(createdAt)}, {formatTime(createdAt)}
        </Text>
      )}

      {/* 🔹 Tags */}
      <Flex wrap="wrap" gap={2}>
        <DropdownChip
          value={adminRole}
          onChange={setAdminRole}
          disabled={data?.user_info?.admin_verified}
          options={CLASSIFIED_ROLE_TYPES}
          label="Primary Role"
        />
        {fields.map(
          (field) =>
            field.value && (
              <Tag
                key={field.key}
                size="md"
                variant="solid"
                colorScheme={field.color}
                borderRadius="full"
                alignItems="center"
                px={2}
                py={1}
                fontWeight="bold"
                fontSize="16px"
              >
                <TagLabel mr={1}>{field.label}</TagLabel>
                {field.value}
              </Tag>
            )
        )}
        <DropdownChip
          value={gender}
          onChange={setGender}
          options={GenderTypes}
          label="Select Gender"
          onConfirm={confirmClassification}
        />
      </Flex>

      {/* 🔹 Subscriptions */}
      {data?.subscriptions?.length > 0 && (
        <Box>
          <Text fontWeight="bold" mb={2} color="gray.700">
            Subscriptions
          </Text>
          <Flex wrap="wrap" gap={2}>
            {data.subscriptions.map((sub) => (
              <Tag
                key={sub._id}
                size="md"
                variant="solid"
                colorScheme={sub.status === "active" ? "green" : "gray"}
                borderRadius="full"
                px={2}
                py={1}
              >
                <TagLabel>
                  {sub.plan_name || "Plan"} · {sub.status}
                  {sub.start_date ? ` · ${formatDate(sub.start_date)}` : ""}
                  {sub.end_date ? ` → ${formatDate(sub.end_date)}` : ""}
                </TagLabel>
              </Tag>
            ))}
          </Flex>
        </Box>
      )}

      {/* 🔹 Table */}
      <TableContainer
        bg="white"
        rounded="lg"
        shadow="md"
        borderWidth="1px"
        overflow="hidden"
      >
        <Table variant="simple">
          <Thead bg="gray.50">
            <Tr>
              <Th color="gray.700">Metric</Th>
              <Th color="gray.700">Highlights</Th>
            </Tr>
          </Thead>
          <Tbody>
            {tableRowData
              .filter((row) => {
                if (
                  row.metric === "AI comment" ||
                  row.metric === "Admin Comment"
                )
                  return true;
                if (!row.count && (row.highlights === "-" || !row.highlights))
                  return false;
                return true;
              })
              .map((row, idx) => (
                <Tr
                  key={idx}
                  bg={idx % 2 === 0 ? "white" : "gray.50"}
                  _hover={{ bg: "gray.100" }}
                  transition="background-color 0.2s"
                >
                  <Td fontWeight="medium" color="gray.800">
                    {row.metric}{" "}
                    <Tag
                      px={3}
                      py={1}
                      rounded="full"
                      fontWeight="semibold"
                      textAlign="center"
                      colorScheme="blue"
                    >
                      {row.count}
                    </Tag>
                  </Td>

                  <Td>
                    <Tooltip
                      label={row.highlights}
                      hasArrow
                      placement="top"
                      bg="gray.700"
                      color="white"
                    >
                      <Text
                        width="full"
                        fontSize="sm"
                        noOfLines={1}
                        color="gray.800"
                        cursor="pointer"
                        style={{
                          maxWidth: "calc(100vw - 350px)",
                        }}
                      >
                        {row.highlights}
                      </Text>
                    </Tooltip>
                  </Td>
                </Tr>
              ))}
            <AdminComment
              value={adminComment}
              id={classification?.user_id || data?.user_info?._id}
              onEditComment={onEditComment}
            />
          </Tbody>
        </Table>
      </TableContainer>
    </Flex>
  );
};

export default JSONTable;
